var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var PacemAzureMapAdapterElement_1;
import { Utils, CustomElement, Watch, P, PropertyConverters, CustomElementUtils, Debounce } from '@pacem/pacem-core';
import { PacemOverlayAdapter, PacemMapAdapterElement, PacemLayerAdapter, PacemMarkerAdapter, PacemShapeAdapter, MapUtils, MapColorScheme, MapType, MapEvent } from './types-components';
// namespace Pacem.Components.Maps {
const consts = {
    TIMEOUT: 1000,
    API_JS: 'https://atlas.microsoft.com/sdk/javascript/mapcontrol/3/atlas.min.js',
    API_CSS: 'https://atlas.microsoft.com/sdk/javascript/mapcontrol/3/atlas.min.css'
};
function convertAddress(address) {
    return {
        city: address.municipality,
        state: address.countrySubdivision,
        province: address.countrySecondarySubdivision,
        village: address.municipalitySubdivision ?? address.localName,
        zipCode: address.postalCode,
        streetName: address.street ?? address.streetName,
        houseNumber: address.buildingNumber ?? address.streetNumber,
        countryCode: address.countryCode,
        country: address.country,
    };
}
class AtlasMapUtils {
    static getPosition(latLng) {
        if (Array.isArray(latLng)) {
            latLng = { lat: latLng[0], lng: latLng[1] };
        }
        return atlas.data.Position.fromLatLng(latLng);
    }
    static getBounds(bounds) {
        if (Utils.isArray(bounds)) {
            return atlas.data.BoundingBox.fromPositions(bounds.map(p => /* reverse */ [p[1], p[0]]));
        }
        const ne = AtlasMapUtils.getPosition(bounds.ne), sw = AtlasMapUtils.getPosition(bounds.sw);
        return atlas.data.BoundingBox.fromPositions([ne, sw]);
    }
    static getLatLng(position) {
        return { lat: position[1], lng: position[0] };
    }
    static getLatLngBounds(bounds) {
        const ne = atlas.data.BoundingBox.getNorthEast(bounds), sw = atlas.data.BoundingBox.getSouthWest(bounds);
        return { ne: AtlasMapUtils.getLatLng(ne), sw: AtlasMapUtils.getLatLng(sw) };
    }
    static parseGeocoding(response) {
        if (Utils.isNullOrEmpty(response.results)) {
            return [];
        }
        ;
        return response.results.map(r => {
            return {
                fullAddress: r.address.freeformAddress,
                address: convertAddress(r.address),
                position: { lat: r.position.lat, lng: r.position.lon, altitude: null }
            };
        });
    }
    static parseReverseGeocoding(response, location) {
        if (response.summary.numResults < 1) {
            return { fullAddress: '', address: {}, position: null };
        }
        ;
        const addr = response.addresses[0], position = (location ?? addr.position).split(',').map(i => parseFloat(i)), address = addr.address, fullAddress = address.freeformAddress;
        return {
            fullAddress, address: convertAddress(address), position: { lat: position[0], lng: position[1], altitude: position.length > 2 ? position[2] : null }
        };
    }
}
class PacemAzureOverlayAdapter extends PacemOverlayAdapter {
    deleteOverlay(overlay, map) {
        throw new Error("Method not implemented.");
    }
    buildOverlay(item, map) {
        throw new Error("Method not implemented.");
    }
    setBounds(item, map, bounds) {
        throw new Error("Method not implemented.");
    }
    updateOverlay(overlay, map, item) {
        throw new Error("Method not implemented.");
    }
}
class PacemAzureLayerAdapter extends PacemLayerAdapter {
    addLoadEventListener(layer, map, callback) {
        throw new Error("Method not implemented.");
    }
    removeLoadEventListener(layer, map, callback) {
        throw new Error("Method not implemented.");
    }
    deleteLayer(layer, map) {
        throw new Error("Method not implemented.");
    }
    buildLayer(item, map) {
        throw new Error("Method not implemented.");
    }
}
class PacemAzureMarkerAdapter extends PacemMarkerAdapter {
    getMarkerPosition(marker) {
        const position = marker.getOptions().position;
        return AtlasMapUtils.getLatLng(position);
    }
    buildMarker(item, map) {
        const marker = new atlas.HtmlMarker();
        map.markers.add(marker);
        marker.setOptions({ popup: new atlas.Popup() });
        return marker;
    }
    addClickEventListener(marker, map, callback) {
        map.events.add('click', marker, callback);
    }
    addDragEventListener(marker, map, callback) {
        map.events.add('drag', marker, callback);
    }
    addDragEndEventListener(marker, map, callback) {
        map.events.add('dragend', marker, callback);
    }
    _setPopupPosition(info, position) {
        const popup = this._getPopup(info);
        if (!Utils.isNull(popup)) {
            popup.setOptions({ position });
        }
    }
    setPosition(marker, position) {
        const p = AtlasMapUtils.getPosition(position);
        marker.setOptions({ position: p });
        this._setPopupPosition(marker, p);
    }
    setIcon(marker, icon) {
        let popupOptions = { pixelOffset: [0, -36 /* default marker size */] };
        let options = { popup: new atlas.Popup() };
        let fnIcon = (icon, w, h) => `<img width="${(w || 'auto')}" height="${(h || 'auto')}" style="pointer-events: none" src="${icon}" />`; //item.icon.url;
        if (typeof icon === 'string') {
            // icon url only
            options.htmlContent = fnIcon(icon);
        }
        else if (!Utils.isNull(icon)) {
            // structured icon
            const size = icon.size, anchor = icon.anchor;
            options.htmlContent = fnIcon(icon.url, size && size.width, size && size.height);
            if (anchor) {
                options.anchor = "top-left";
                options.pixelOffset = [-anchor.x, -anchor.y];
                popupOptions.pixelOffset = [0, -anchor.y];
            }
            else if (size && size.height) {
                popupOptions.pixelOffset = [0, -size.height / 2];
            }
        }
        options.popup.setOptions(popupOptions);
        marker.setOptions(options);
    }
    setLabel(marker, label) {
        let options = { text: label };
        marker.setOptions(options);
    }
    setDraggable(marker, draggable) {
        let options = { draggable };
        marker.setOptions(options);
    }
    deleteMarker(marker, map) {
        map.markers.remove(marker);
    }
    deleteInfoWindow(info, map) {
        const popup = this._getPopup(info);
        popup.remove();
    }
    closeInfo(info) {
        const popup = this._getPopup(info);
        if (popup?.isOpen() !== true) {
            return;
        }
        popup.close();
    }
    openInfo(info, content, map, marker) {
        const { popup, position } = info.getOptions();
        this._setContent(popup, content, position);
        marker.togglePopup();
        map.events.addOnce('close', popup, (e) => {
            popup.setOptions({ content: '' });
        });
    }
    buildInfoWindow(item) {
        return this.markers.get(item);
    }
    addCloseClickEventListener(info, map, callback) {
        const popup = this._getPopup(info);
        map.events.add('close', popup, callback);
    }
    setContent(info, content) {
        /* unnecessary */
        const { popup, position } = info.getOptions();
        this._setContent(popup, content, position);
    }
    _getPopup(info) {
        return info.getOptions().popup;
    }
    _setContent(popup, content, position) {
        popup.setOptions({ content: `<div style="padding: 8px 24px 8px 8px;">${content}</div>`, position });
    }
}
class PacemAzureShapeAdapter extends PacemShapeAdapter {
    addDragStartEventListener(shape, callback) {
        throw new Error("Method not implemented.");
    }
    deleteShape(shape, map) {
        throw new Error("Method not implemented.");
    }
    getShapeBounds(shape) {
        throw new Error("Method not implemented.");
    }
    getShapePosition(obj) {
        throw new Error("Method not implemented.");
    }
    buildShape(item, map) {
        throw new Error("Method not implemented.");
    }
    addClickEventListener(shape, callback) {
        throw new Error("Method not implemented.");
    }
    addDragEventListener(shape, callback) {
        throw new Error("Method not implemented.");
    }
    addDragEndEventListener(shape, callback) {
        throw new Error("Method not implemented.");
    }
    setShape(shape, item) {
        throw new Error("Method not implemented.");
    }
    setEditable(shape, editable) {
        throw new Error("Method not implemented.");
    }
    setDraggable(shape, draggable) {
        throw new Error("Method not implemented.");
    }
}
//#region geocoders
class PacemAzureMapGeocoder {
    _buildGeocodeRequest(key, search, maxResults = 10, language = '') {
        // TODO: upgrade to newest version (see: https://www.npmjs.com/package/azure-maps-rest)
        const url = 'https://atlas.microsoft.com/search/address/json?api-version=1.0';
        const query = { 'subscription-key': key, query: search, limit: '' + maxResults, language };
        return {
            uri: Utils.URIs.appendQuery(url, query),
            options: {
                mode: 'cors' //, headers: {}
            }
        };
    }
    parseGeocodeResponse(response) {
        return new Promise((resolve, reject) => {
            response.json().then((r) => {
                const retval = AtlasMapUtils.parseGeocoding(r);
                resolve(retval);
            }, e => reject(e));
        });
    }
    geocode(key, address, maxResults = 10, language = '') {
        return new Promise((resolve, reject) => {
            const request = this._buildGeocodeRequest(key, address, maxResults, language);
            return fetch(request.uri, request.options).then(response => {
                if (response.ok) {
                    this.parseGeocodeResponse(response).then(resolve, reject);
                }
                else {
                    reject(response);
                }
            }, e => reject(e));
        });
    }
    reverseGeocode(key, latLng, language = '') {
        return new Promise((resolve, reject) => {
            const request = this._buildReverseRequest(key, latLng, language);
            return fetch(request.uri, request.options).then(response => {
                if (response.ok) {
                    this._parseReverseResponse(response).then(resolve, reject);
                }
                else {
                    reject(response);
                }
            }, e => reject(e));
        });
    }
    _buildReverseRequest(key, position, language = '') {
        // TODO: upgrade to newest version (see: https://atlas.microsoft.com/reverseGeocode?api-version=2023-06-01)
        const url = 'https://atlas.microsoft.com/search/address/reverse/json?api-version=1.0';
        const latLng = MapUtils.parseCoords(position);
        const query = {
            'subscription-key': key,
            query: latLng[0] + ',' + latLng[1],
            language
        };
        return {
            uri: Utils.URIs.appendQuery(url, query),
            options: {
                mode: 'cors' //, headers: {}
            }
        };
    }
    _parseReverseResponse(response) {
        return new Promise((resolve, reject) => {
            response.json().then((r) => {
                const retval = AtlasMapUtils.parseReverseGeocoding(r);
                resolve([retval]);
            }, e => reject(e));
        });
    }
}
//#endregion
const MAP_EVENTS = ['moveend', 'dragend', 'zoomend', 'rotateend'];
function getNativeMapStyle(options) {
    const darkTheme = options.theme == MapColorScheme.DARK || (options.theme !== MapColorScheme.LIGHT && window.matchMedia('(prefers-color-scheme: dark)').matches);
    switch (options.type) {
        case 'satellite':
        case 'satellite_road_labels':
        case 'grayscale_dark':
        case 'grayscale_light':
        case 'night':
        case 'road_shaded_relief':
        case 'road':
        case 'high_constrast_dark':
        case 'high_constrast_light':
            return options.type;
        case MapType.HYBRID:
            return 'satellite_road_labels';
        case MapType.SATELLITE:
            return 'satellite';
        case MapType.TERRAIN:
            return 'road_shaded_relief';
        case MapType.HIGH_CONTRAST:
            return darkTheme ? 'high_constrast_dark' : 'high_constrast_light';
        default:
            return darkTheme ? 'night' : 'road';
    }
}
let PacemAzureMapAdapterElement = PacemAzureMapAdapterElement_1 = class PacemAzureMapAdapterElement extends PacemMapAdapterElement {
    geocode(address, maxResults) {
        return new PacemAzureMapGeocoder().geocode(this.subscriptionKey, address, maxResults, Utils.lang(this));
    }
    reverseGeocode(latLng, maxResults) {
        return new PacemAzureMapGeocoder().reverseGeocode(this.subscriptionKey, latLng, Utils.lang(this));
    }
    getBounds(ctrl) {
        const map = this.getMap(ctrl);
        const { bounds } = map.getCamera();
        return AtlasMapUtils.getLatLngBounds(bounds);
    }
    isActive() {
        return !Utils.isNullOrEmpty(this.subscriptionKey);
    }
    buildOverlayAdapterFor(map) {
        return new PacemAzureOverlayAdapter(map, this);
    }
    buildMarkerAdapterFor(map) {
        return new PacemAzureMarkerAdapter(map, this);
    }
    buildShapeAdapterFor(map) {
        return new PacemAzureShapeAdapter(map, this);
    }
    buildLayerAdapterFor(map) {
        return new PacemAzureLayerAdapter(map, this);
    }
    setViewNative(ctrl, options) {
        const map = this.getMap(ctrl);
        // style
        options = Utils.extend({ type: ctrl.type, theme: ctrl.theme }, options);
        const style = getNativeMapStyle(options);
        map.setStyle({ style });
        const center = options.center && AtlasMapUtils.getPosition(options.center) || map.getCamera().center;
        const zoom = options.zoom || map.getCamera().zoom;
        map.setCamera({ center, zoom });
    }
    #listeners = new WeakMap();
    snapshot(ctrl, type = 'image/png', quality) {
        if (!this.hasMap(ctrl)) {
            return Promise.reject();
        }
        const { map } = this.tryGetMap(ctrl);
        return new Promise((resolve, _) => {
            Utils.blobToDataURL(this.#blobs.get(ctrl)).then(dataUrl => {
                const container = map.getCanvasContainer();
                container.style.backgroundImage = `url(${dataUrl})`;
                container.style.backgroundSize = 'contain';
                Utils.snapshotElement(container, null, type, quality).then(resolve);
            });
        });
    }
    #blobs = new WeakMap();
    // #region ABSTRACT IMPLEMENTATION
    dispose(ctrl) {
        if (!this.hasMap(ctrl)) {
            return;
        }
        const { map } = this.tryGetMap(ctrl);
        // detach listeners
        const listeners = this.#listeners.get(ctrl);
        listeners.forEach(listener => {
            map.events.remove(listener.eventName, listener.handler);
        });
        // cleanup
        this.#listeners.delete(ctrl);
        // destroy map
        map.dispose();
    }
    async init(container) {
        await Promise.all([
            CustomElementUtils.importjs(consts.API_JS),
            CustomElementUtils.importcss(consts.API_CSS, undefined, true)
        ]);
        const ctrl = container;
        const scale = ctrl.scale;
        const draggable = ctrl.draggable;
        const dblClickZoom = ctrl.doubleClickZoom;
        const kbShortcuts = ctrl.keyboardShortcuts;
        //
        const center = AtlasMapUtils.getPosition(ctrl.center);
        const mapOptions = {
            center: center,
            zoom: ctrl.zoom,
            subscriptionKey: this.subscriptionKey,
            dragPanInteraction: draggable,
            scrollZoomInteraction: ctrl.mousewheel,
            dblClickZoomInteraction: !dblClickZoom,
            keyboardInteraction: kbShortcuts,
            language: this.language || Utils.lang(container),
        };
        const canvas = ctrl.container;
        const mapElement = document.createElement('div');
        mapElement.id = `azure-maps-${Utils.uniqueCode()}`;
        mapElement.style.width = '100%';
        mapElement.style.height = '100%';
        canvas.innerHTML = '';
        canvas.appendChild(mapElement);
        const map = new atlas.Map(mapElement.id, mapOptions);
        const listeners = [];
        this.#listeners.set(ctrl, listeners);
        MAP_EVENTS.forEach((evt) => {
            const handler = this._spinMapUpdateHandler(ctrl, map);
            map.events.add(evt, handler);
            listeners.push({ eventName: evt, handler });
        });
        if (scale && ctrl.zoomControl) {
            map.events.addOnce('ready', (e) => {
                let pos = atlas.ControlPosition.NonFixed;
                switch (ctrl.zoomControl) {
                    case 'topleft':
                        pos = atlas.ControlPosition.TopLeft;
                        break;
                    case 'topright':
                        pos = atlas.ControlPosition.TopRight;
                        break;
                    case 'bottomright':
                        pos = atlas.ControlPosition.BottomRight;
                        break;
                    case 'bottomleft':
                        pos = atlas.ControlPosition.BottomLeft;
                        break;
                }
                map.controls.add(new atlas.control.ZoomControl(), { position: pos });
            });
        }
        map.events.add('idle', (e) => {
            map.getCanvas().toBlob(blob => {
                this.#blobs.set(container, blob);
            });
        });
        map.events.addOnce('ready', () => {
            container.dispatchEvent(new MapEvent("maploaded"));
        });
        return { map, container: mapElement };
    }
    invalidateSize(ctrl) {
        if (!this.hasMap(ctrl)) {
            return;
        }
        const { map } = this.tryGetMap(ctrl);
        if (map) {
            map.resize();
        }
    }
    propertyChangedCallback(name, old, val, first) {
        super.propertyChangedCallback(name, old, val, first);
        if (!first && name === 'language') {
            for (let ctrl of this.enrolledMaps()) {
                const { map } = this.tryGetMap(ctrl);
                map.setStyle({ language: val || Utils.lang(ctrl) });
            }
        }
    }
    //#endregion
    _spinMapUpdateHandler(ctrl, map) {
        return () => {
            this._updateMap(ctrl, map);
        };
    }
    _updateMap(ctrl, map) {
        if (!Utils.isNull(map) && !Utils.isNull(ctrl)) {
            const camera = map.getCamera(), center = camera.center, zoom = camera.zoom;
            if (camera.zoom === 1) {
                console.warn('Azure Maps control lacks in reflecting the actual map zoom onto the camera property. Cannot reflect info back to PacemMapElement.');
            }
            else
                this.updateMapElement(ctrl, AtlasMapUtils.getLatLng(center), zoom);
        }
    }
    static _getBounds(ctrl) {
        const paddingTop = ctrl.paddingTop || 0, paddingLeft = ctrl.paddingLeft || 0, paddingRight = ctrl.paddingRight || 0, paddingBottom = ctrl.paddingBottom || 0;
        return { 'top': paddingTop, 'bottom': paddingBottom, 'left': paddingLeft, 'right': paddingRight };
    }
    fitBoundsNative(ctrl, bnds, padding = PacemAzureMapAdapterElement_1._getBounds(ctrl)) {
        const map = this.getMap(ctrl);
        // TODO: avoid double loop (specially for large sets), just set coords in 'correct' place for Azure maps right away...
        map.setCamera({ bounds: AtlasMapUtils.getBounds(bnds), padding });
        //} else {
        //    if (bnds.length === 1) {
        //        map.setCamera({ center: AtlasMapUtils.getPosition(bnds[0]), zoom: ctrl.zoom });
        //    }
        //}
    }
    ;
};
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], PacemAzureMapAdapterElement.prototype, "subscriptionKey", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], PacemAzureMapAdapterElement.prototype, "language", void 0);
__decorate([
    Debounce(500)
], PacemAzureMapAdapterElement.prototype, "_updateMap", null);
PacemAzureMapAdapterElement = PacemAzureMapAdapterElement_1 = __decorate([
    CustomElement({ tagName: P + '-map-adapter-azure' })
], PacemAzureMapAdapterElement);
export { PacemAzureMapAdapterElement };
//}
